/**
 * Permission helpers.
 *
 * IMPORTANT (handoff §1): this is for UX only. The server enforces scope and
 * permissions and returns 403 regardless of what the UI shows. Never treat
 * hiding a button as a security control.
 */
import { useCallback, useMemo } from "react";
import { useAuth } from "../AuthContext";
import type { PermissionKey } from "../permissions";

export function useCan() {
  const { user } = useAuth();
  const held = useMemo(() => new Set(user?.permissions ?? []), [user]);

  const can = useCallback((p: PermissionKey) => held.has(p), [held]);
  const canAny = useCallback(
    (...ps: PermissionKey[]) => ps.some((p) => held.has(p)),
    [held],
  );
  const canAll = useCallback(
    (...ps: PermissionKey[]) => ps.every((p) => held.has(p)),
    [held],
  );

  return { can, canAny, canAll };
}

/** Renameable display terms, with defaults if the user isn't loaded yet. */
export function useLabels() {
  const { user } = useAuth();
  return (
    user?.labels ?? {
      tier2_singular: "Partner",
      tier2_plural: "Partners",
      readonly_role: "Analyst",
    }
  );
}
